import { useEffect, useState } from 'react';
import { array, Infer, number, object } from 'superstruct';
import { usePriceFeedState } from '../../components/atoms/PriceFeedStore';
import Connect from '../../lib/BridgeNode/connect';
import { ConnectReq, MWBOCombined, SubscribeReq } from '../../lib/mqtt_poc_models';
import { instrumentToExchangeReverseMap, instrumentToMqttFeedItem } from './helpers';
import { usePriceFeedMQTTToken } from './priceFeedMQTTToken';

export const PRICE_FEED_LOCAL_STORAGE_KEY = 'price_feed_mqtt';

const MAX_RETRY_ATTEMPTS = 15;
const RETRY_DELAY_MS = 2000;

const depthSuperStruct = object({
	quantity: number(),
	price: number(),
	orders: number(),
	transactionType: number(),
});

export const IIFLPriceWSResponseSuperStruct = object({
	ltp: number(),
	lastTradedQuantity: number(),
	tradedVolume: number(),
	high: number(),
	low: number(),
	open: number(),
	close: number(),
	averageTradedPrice: number(),
	reserved: number(),
	bestBidQuantity: number(),
	bestBidPrice: number(),
	bestAskQuantity: number(),
	bestAskPrice: number(),
	totalBidQuantity: number(),
	totalAskQuantity: number(),
	priceDivisor: number(),
	lastTradedTime: number(),
	marketDepth: array(depthSuperStruct),
});

export type IIFLPriceWSResponseType = Infer<typeof IIFLPriceWSResponseSuperStruct>;

export const buildIIFLInstrumentKey = (brokerExchange: string, brokerExchangeType: string, brokerCode: number) => {
	return `${brokerExchange}${brokerExchangeType}${brokerCode}`;
};

export default function useWriteMQTTFeed() {
	const instruments = usePriceFeedState((state) => state.instruments);
	const status = usePriceFeedState((state) => state.status);
	const setStatus = usePriceFeedState((state) => state.setStatus);
	const setLastUpdatedTime = usePriceFeedState((state) => state.setLastUpdatedTime);

	const [socketConnection, setSocketConnection] = useState<Connect | undefined>(undefined);
	const [retryCount, setRetryCount] = useState(0);

	const { data: token, mutate } = usePriceFeedMQTTToken();

	useEffect(() => {
		if (token) {
			try {
				const connection = Connect.getInstance();
				setSocketConnection(connection);

				localStorage.setItem(PRICE_FEED_LOCAL_STORAGE_KEY, JSON.stringify({}));

				connection.on('OnFeedDataReceived', (byteArray, topic) => {
					const rawData = new MWBOCombined(byteArray);
					const topicKey = instrumentToExchangeReverseMap(topic);
					const priceDivisor = typeof rawData.priceDivisor === 'number' ? rawData.priceDivisor : 100;
					const ltp = rawData.ltp / priceDivisor;
					const data: IIFLPriceWSResponseType = {
						ltp,
						lastTradedQuantity: rawData.lastTradedQuantity,
						tradedVolume: rawData.tradedVolume,
						high: rawData.high,
						low: rawData.low,
						open: rawData.open,
						close: rawData.close,
						averageTradedPrice: rawData.averageTradedPrice,
						reserved: rawData.reserved,
						bestBidQuantity: rawData.bestBidQuantity,
						bestBidPrice: rawData.bestBidPrice,
						bestAskQuantity: rawData.bestAskQuantity,
						bestAskPrice: rawData.bestAskPrice,
						totalBidQuantity: rawData.totalBidQuantity,
						totalAskQuantity: rawData.totalAskQuantity,
						priceDivisor: rawData.priceDivisor,
						lastTradedTime: rawData.lastTradedTime,
						marketDepth: rawData.marketDepth,
					};

					const existing = JSON.parse(localStorage.getItem(PRICE_FEED_LOCAL_STORAGE_KEY));
					existing[
						buildIIFLInstrumentKey(
							topicKey.brokerExchange,
							topicKey.brokerExchangeType,
							topicKey.brokerCode,
						)
					] = data;
					localStorage.setItem(PRICE_FEED_LOCAL_STORAGE_KEY, JSON.stringify(existing));
					setStatus('live');
					setLastUpdatedTime(new Date());
				});

				connection.on('OnError', (errorCode: any, errorMsg: any) => {
					console.error(`${errorCode}:`, errorMsg);

					if (status !== 'connecting') {
						setStatus('disconnected');
					}

					if (retryCount < MAX_RETRY_ATTEMPTS) {
						console.log(`Retrying connection (${retryCount + 1}/${MAX_RETRY_ATTEMPTS})...`);
						setTimeout(() => {
							setRetryCount((prev) => prev + 1);
							mutate();
						}, RETRY_DELAY_MS);
					} else {
						console.error('Max retry attempts reached');
						mutate();
					}
				});

				//Events End

				setStatus('connecting');
				connection
					.connectHost(JSON.stringify(new ConnectReq(token, 'bridge.iiflcapital.com', 443)), (res: any) => {
						console.error('Disconnection Event:', res); // Logs connection & disconnection events
						setStatus('disconnected');
					})
					.then((conn: any) => {
						if (JSON.parse(conn)?.message === 'Success') {
							console.log('MQTT CONNECTED');
							setStatus('live');
							setRetryCount(0);
						}
					})
					.catch((err: any) => {
						console.error('Connection Failed:', err);
						if (retryCount < MAX_RETRY_ATTEMPTS) {
							console.log(`Retrying connection (${retryCount + 1}/${MAX_RETRY_ATTEMPTS})...`);
							setTimeout(() => {
								setRetryCount((prev) => prev + 1);
								mutate();
							}, RETRY_DELAY_MS);
						} else {
							console.error('Max retry attempts reached');
						}
					});
			} catch (error) {
				console.error('Error:', error);
			}
		}
	}, [token, retryCount]);

	useEffect(() => {
		if (socketConnection !== undefined && socketConnection.isConnected && instruments.length > 0) {
			const subscribe_instruments = instruments.map((item) => instrumentToMqttFeedItem(item));
			const subRequest = JSON.stringify(new SubscribeReq(subscribe_instruments));
			socketConnection.SubscribeFeed(subRequest).catch((error) => {
				console.error('Subscription Failed:', error);
			});
		}
	}, [JSON.stringify(instruments), socketConnection, status]);

	// Attempt to reconnect when status is disconnected
	useEffect(() => {
		if (status === 'disconnected' && token && retryCount < MAX_RETRY_ATTEMPTS) {
			console.log(`Status disconnected, attempting reconnect (${retryCount + 1}/${MAX_RETRY_ATTEMPTS})...`);
			const timeoutId = setTimeout(() => {
				setRetryCount((prev) => prev + 1);
				mutate();
			}, RETRY_DELAY_MS);

			return () => clearTimeout(timeoutId);
		}
	}, [status, token, retryCount, mutate]);
}
